<template>
  <CModal size="xl" :visible="showEndWorkingDayModal" @close="closeReportForm">
    <CModalHeader>
      <CModalTitle>{{ $t('dashboard.timeManagement') }}</CModalTitle>
    </CModalHeader>
    <CModalBody>
      <CForm>
        <div class="mb-3">
          <h6 ref="task">{{ $t('dashboard.chooseTask') }}</h6>
          <TypeaheadInput :disabled="editedReport" v-if="tasksArray.length" v-model="selectedTask" :items="tasksArray" :itemText="'name'" itemValue="id" placeholder="" />
        </div>

        <div v-show="currentDayData.reports?.length > 0 && !isEditing" class="mb-3 table-wrapper">
          <CTable hover>
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell scope="col" v-for="col in tableCols" :key="col.index">{{ $t(`${col.name}`) }}</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              <CTableRow v-for="(item, index) in currentDayData.reports" :key="index + 1">
                <CTableDataCell>{{ item.task.tag + ' / ' + item.task.name }}</CTableDataCell>
                <CTableDataCell>{{ Math.floor(item.seconds / 3600) }} : {{ checkLength(Math.floor((item.seconds / 60) % 60)) }}</CTableDataCell>
                <CTableDataCell><i>{{ item.date }}</i></CTableDataCell>
              </CTableRow>

              <CTableRow>
                <CTableDataCell><b>{{ $t('dashboard.totalTimeToday') }}</b></CTableDataCell>
                <CTableDataCell><b>{{ currentDayData.today_duration_time }}</b></CTableDataCell>
              </CTableRow>

              <CTableRow v-if="currentDayData.today_duration_time != currentDayData.duration_time">
                <CTableDataCell><b>{{ $t('dashboard.totalTime') }}</b></CTableDataCell>
                <CTableDataCell><b>{{ currentDayData.duration_time }}</b></CTableDataCell>
              </CTableRow>
            </CTableBody>
          </CTable>
        </div>

        <div v-show="selectedTask">
          <div class="mb-3 ">
            <div class="d-flex justify-content-between align-items-center gap-4">
            <h6 class="report-label">{{ $t('attendance.chooseDate') }}</h6>
            <h6 class="report-label">{{ $t('dashboard.overtime') || 'Overtime' }}</h6>
          </div>  
            <div class="d-flex align-items-center gap-4">
              <input :class="{ disabled: editedReport }" :disabled="editedReport" id="reportDate" class="date-input date report-item" type="date" :max="new Date().toISOString().slice(0, 10)" ref="since" @blur="setDate" v-model="from" style="border-radius: 0.25rem; width: 49%;" />

              <!-- Overtime -->
              <div class="form-check d-flex align-items-center" style="gap: 0.5rem;">
                <input class="form-check-input" type="checkbox" v-model="overtime" id="overtimeCheck" />
              </div>
            </div>
          </div>

          <div class="mb-3">
            <h6>{{ $t('dashboard.spentTime') }}</h6>
            <div class="widget-inner-input-wrapper">
              <input v-model="v$.hours.$model" @input="checkHours" min="0" max="23" maxlength="2" class="date-input" ref="hours" type="number" placeholder="0" />
              <span class="widget-inner-input-text">:</span>
              <input v-model="minutes" @input="checkMinutes" min="0" max="59" maxlength="2" class="date-input" ref="minutes" type="number" placeholder="00" />
            </div>
            <small v-if="check && !hours && +minutes === 0" class="helper-text invalid">
              {{ $t('dashboard.spentTimeValidation') }}
            </small>
          </div>

          <div class="mb-3">
            <h6>{{ $t('dashboard.comment') }}</h6>
            <vue-editor v-model="reportText" />
            <small v-if="v$.reportText.$dirty && v$.reportText.$error" class="helper-text invalid">
              {{ $t('dashboard.reportValidation') }}
            </small>
          </div>

          <div class="mb-3">
            <CButton v-if="!isEditing" @click.prevent="setReport" class="float-end" size="sm" color="primary" :disabled="disableSubmitReport">OK</CButton>
            <CButton v-else @click.prevent="editReport" class="float-end" size="sm" color="primary" :disabled="disableSubmitReport">Edit</CButton>
          </div>
        </div>
      </CForm>
    </CModalBody>

    <CModalFooter v-if="!isEditing">
      <div class="mb-3">
        <CButton @click.prevent="endWorkingDay" class="float-end" size="sm" color="primary">
          {{ $t('dashboard.dayEnd') }}
        </CButton>
      </div>
    </CModalFooter>
  </CModal>
</template>

<script>
import { VueEditor } from 'vue3-editor'
import cols from '@/helpers/tableCols'
import { reactive, toRefs, computed } from 'vue'
import { useStore } from 'vuex'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import fireSwal from '@/helpers/fireSwal'
import TypeaheadInput from 'vue3-typeahead-input'
import 'vue3-typeahead-input/dist/style.css'
import { useQueryClient } from '@tanstack/vue-query'

export default {
  name: 'Add Report Modal',
  components: { VueEditor, TypeaheadInput },
  emits: ['closeReportForm', 'refreshData'],
  props: {
    showEndWorkingDayModal: Boolean,
    clickedTask: Object,
    editedReport: Object
  },
  data() {
    return {
      pickerFrom: null,
      from: '',
      hours: null,
      minutes: null,
      tasks: [],
      tasksArray: [],
      check: false,
      overtime: false
    }
  },
  watch: {
    editedReport(newVal) {
      if (newVal) {
        this.selectedTask = newVal.task.id
        this.from = newVal.date
        this.hours = Math.floor(newVal.seconds / 3600)
        this.minutes = Math.floor((newVal.seconds / 60) % 60)
        this.reportText = newVal.report
        this.overtime = !!newVal.overtime
      }
    },
    clickedTask(newVal) {
      if (newVal && this.tasksData?.length) {
        if (this.tasksArray.some(task => task.id === newVal.id)) {
          this.selectedTask = newVal.id
        }
      }
    },
    showEndWorkingDayModal(newVal) {
      if (newVal && !this.tasksData?.length) {
        this.store.dispatch('tasks/getCurrentUserTasks', this.user.id).then(() => {
          this.updateTasksArray()
          if (this.clickedTask && this.tasksArray.some(task => task.id === this.clickedTask.id)) {
            this.selectedTask = this.clickedTask.id
          }
        })
      } else if (newVal && this.tasksData?.length) {
        this.updateTasksArray()
        if (this.clickedTask && this.tasksArray.some(task => task.id === this.clickedTask.id)) {
          this.selectedTask = this.clickedTask.id
        }
      }
    }
  },
  mounted() {
    this.from = new Date().toISOString().slice(0, 10)
  },
  setup() {
    const queryClient = useQueryClient()
    const store = useStore()
    const state = reactive({
      v$: useVuelidate(),
      since: null,
      selectedTask: null,
      disableSubmitReport: false,
      reportText: '',
      tableCols: computed(() => cols.reportPreviewCols),
      currentUser: computed(() => store.getters['authorization/getUserNickname']),
    })
    return { ...toRefs(state), store, queryClient }
  },
  validations() {
    return {
      hours: {},
      minutes: {},
      reportText: { required },
    }
  },
  computed: {
    isEditing() {
      return !!this.editedReport
    },
    tasksData() {
      return this.$store.getters['tasks/getCurrentUserTasks']
    },
    user() {
      return this.store.getters['authorization/getCurrentUser']
    },
    currentDayData() {
      return this.$store.getters['reports/getCurrentDayData']
    }
  },
  methods: {
    updateTasksArray() {
      if (this.tasksData?.length) {
        this.tasksArray = this.tasksData.map(item => ({
          name: item.project?.name ? `${item.tag} / ${item.project.name} - ${item.name}` : `No Project - ${item.name}`,
          id: item.id
        }))
      }
    },
    setDate() {
      if (this.$refs.since?.value) this.from = this.$refs.since.value
    },
    checkLength(val) {
      return String(val).padStart(2, '0')
    },
    endWorkingDay() {
      if (this.currentDayData.reports.length > 0) {
        this.$store.dispatch('reports/endWorkingDay')
        this.$emit('closeReportForm', false)
      } else {
        fireSwal(this.$t('general.reportValidation'), 'error')
      }
    },
    setReport() {
      this.check = true
      if (this.v$.$invalid) {
        this.v$.$touch()
        return
      }

      if (!this.$refs.hours.value && !this.$refs.minutes.value) return
      if (this.$refs.hours.value && !this.$refs.minutes.value) this.$refs.minutes.value = 0

      this.disableSubmitReport = true

      this.store.dispatch('reports/setReports', {
        task_id: this.selectedTask,
        report: this.reportText,
        date: this.from,
        seconds: Number(this.$refs.hours.value) * 3600 + Number(this.$refs.minutes.value) * 60,
        overtime: this.overtime
      }).then(() => {
        this.resetForm()
        this.queryClient.invalidateQueries()
      }).catch(() => fireSwal(this.$t(`notification.tooBigContent`), 'error'))
    },
    editReport() {
      this.check = true
      if (this.v$.$invalid) {
        this.v$.$touch()
        return
      }

      if (!this.$refs.hours.value && !this.$refs.minutes.value) return
      if (this.$refs.hours.value && !this.$refs.minutes.value) this.$refs.minutes.value = 0

      this.disableSubmitReport = true

      this.store.dispatch('reports/editReports', {
        attendance_id: this.editedReport.id,
        task_id: this.selectedTask,
        report: this.reportText,
        date: this.from,
        seconds: Number(this.$refs.hours.value) * 3600 + Number(this.$refs.minutes.value) * 60,
        overtime: this.overtime
      }).then(() => {
        this.resetForm()
        this.queryClient.invalidateQueries()
        fireSwal(this.$t(`reports.reportUpdated`), 'success')
        this.$emit('refreshData')
      }).catch(() => fireSwal(this.$t(`notification.tooBigContent`), 'error'))
    },
    resetForm() {
      this.pickerFrom = null
      this.check = false
      this.v$.$reset()
      this.hours = null
      this.minutes = null
      this.selectedTask = null
      this.reportText = ''
      this.disableSubmitReport = false
      this.from = new Date().toISOString().slice(0, 10)
      this.$refs.hours.value = null
      this.$refs.minutes.value = null
      this.overtime = false
    },
    closeReportForm() {
      this.$emit('closeReportForm', false)
      this.v$.$reset()
      this.check = false
    },
    checkHours(e) {
      const val = +e.target.value
      this.hours = Math.min(Math.max(val, 0), 23)
    },
    checkMinutes(e) {
      const val = +e.target.value
      this.minutes = Math.min(Math.max(val, 0), 59)
    }
  },
  async created() {
  }
}
</script>

<style scoped>
.disabled {
  background-color: #f3f4f6;
}

.form-check {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-width: 542px;

  justify-content: start;
}

.form-check-input {
  transform: scale(1.4);
  cursor: pointer;
}

.report-label {
  width: 50%;
}

/* mobile */

@media (max-width: 768px) {
  :deep(.modal-dialog) {
    margin: 10px;
  }
  :deep(.modal-xl) {
    max-width: 96vw;
    width: 96vw;
    --cui-modal-width: 96vw;
    --bs-modal-width: 96vw;
  }
}

.table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.table-wrapper :deep(table) {
  min-width: 540px; 
}


@media (max-width: 768px) {
  .mb-3 > .d-flex {
    flex-wrap: wrap;
    gap: 8px !important;
  }
  .report-label {
    width: 100%;
    margin-bottom: 4px;
  }
}

@media (max-width: 768px) {
  input.date-input.report-item {
    width: 100% !important;
  }
}

.widget-inner-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}
@media (max-width: 768px) {
  .widget-inner-input-wrapper .date-input {
    width: 72px;
    text-align: center;
  }
}
@media (max-width: 430px) {
  .widget-inner-input-wrapper .date-input {
    width: 64px;
  }
}
.widget-inner-input-text {
  font-weight: 600;
  line-height: 1;
}

@media (max-width: 768px) {
  .form-check {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }
  .form-check-input {
    transform: scale(1.2);
  }
}

@media (max-width: 768px) {
  :deep(.vue3-typeahead-input),
  :deep(.typeahead-input) {
    width: 100%;
  }
  :deep(.vue3-typeahead-input input),
  :deep(.typeahead-input input) {
    width: 100%;
  }
}

@media (max-width: 576px) {
  .float-end { float: none !important; width: 100%; }
  :deep(.modal-footer .btn) { width: 100%; }
}

@media (max-width: 430px) {
  .report-label { font-size: 14px; }
}

/* mobile  */
@media (max-width: 768px) {
  :deep(.modal-dialog) {
    margin: 10px;
  }
  :deep(.modal-xl) {
    max-width: 96vw;
    width: 96vw;
    --cui-modal-width: 96vw;
    --bs-modal-width: 96vw;
  }
}

.table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.table-wrapper :deep(table) {
  min-width: 540px; 
}

@media (max-width: 768px) {
  .mb-3 > .d-flex {
    flex-wrap: wrap;
    gap: 8px !important;
  }
  .report-label {
    width: 100%;
    margin-bottom: 4px;
  }
}

@media (max-width: 768px) {
  input.date-input.report-item {
    width: 100% !important;
  }
}

.widget-inner-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}
@media (max-width: 768px) {
  .widget-inner-input-wrapper .date-input {
    width: 72px;
    text-align: center;
  }
}
@media (max-width: 430px) {
  .widget-inner-input-wrapper .date-input {
    width: 64px;
  }
}
.widget-inner-input-text {
  font-weight: 600;
  line-height: 1;
}

@media (max-width: 768px) {
  .form-check {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }
  .form-check-input {
    transform: scale(1.2);
  }
}

@media (max-width: 768px) {
  :deep(.vue3-typeahead-input),
  :deep(.typeahead-input) {
    width: 100%;
  }
  :deep(.vue3-typeahead-input input),
  :deep(.typeahead-input input) {
    width: 100%;
  }
}

@media (max-width: 576px) {
  .float-end { float: none !important; width: 100%; }
  :deep(.modal-footer .btn) { width: 100%; }
}

@media (max-width: 430px) {
  .report-label { font-size: 14px; }
}

</style>
